import { Component, OnInit, input, output, signal } from '@angular/core';

import { FilesSlideComponent } from '@camelot/files-basic';
import { AttachmentsResult, DocumentsAttachmentsModal, DocumentsAttachmentsModalInput } from '@camelot/files-extended';
import { InputUploadValue } from '@camelot/form-model';
import { CamIconType, FontIconComponent, LocalIconComponent } from '@camelot/icons';
import { DocumentDto, FileType } from '@camelot/services';
import { TranslatePipe } from '@camelot/translation';
import {
  ButtonComponent,
  CardComponent,
  CardContentComponent,
  LayoutFullPanelComponent,
  TextComponent,
} from '@camelot/ui';
import {
  CamAbstractComponent,
  EFileExtension,
  ModalState,
  StopPropagationDirective,
  getFileExtension,
} from '@camelot/utils';

import {
  ConversationDocumentsKey,
  ConversationDocumentsPools,
  buildConversationSectionsHelper,
  conversationDocumentsRecord,
} from '../../helpers/documents-sections.helper';
import { CamTranslationCommunication } from '../../translation.service';

@Component({
  selector: 'cam-communication-attachments-cards',
  templateUrl: './attachments-cards.component.html',
  styleUrl: './attachments-cards.component.scss',
  imports: [
    ButtonComponent,
    CardComponent,
    CardContentComponent,
    DocumentsAttachmentsModal,
    FilesSlideComponent,
    FontIconComponent,
    LayoutFullPanelComponent,
    LocalIconComponent,
    StopPropagationDirective,
    TextComponent,
    TranslatePipe,
  ],
})
export class AttachmentsCardsComponent extends CamAbstractComponent implements OnInit {
  initialAttachments = input<InputUploadValue[]>([]);

  documentsIds = input.required<ConversationDocumentsPools>();

  attachments = output<InputUploadValue[]>();

  public attachmentsValue: InputUploadValue[] = [];
  public conversationAttachmentsModal = new ModalState<
    DocumentsAttachmentsModalInput<ConversationDocumentsKey>,
    AttachmentsResult
  >();
  public slideModal = new ModalState<{ files: DocumentDto[]; initial: DocumentDto | null }, null>();

  public sectionsHelper = signal(buildConversationSectionsHelper({}));

  readonly CamIconType = CamIconType;

  constructor() {
    super();
    CamTranslationCommunication.getInstance();
  }

  ngOnInit(): void {
    this.attachmentsValue = this.initialAttachments();
  }

  public open(): void {
    const documentsIds = this.documentsIds();
    this.sectionsHelper.set(buildConversationSectionsHelper(documentsIds));
    this.conversationAttachmentsModal.asked({
      documentsIds: conversationDocumentsRecord(documentsIds),
      initialAttachments: this.attachmentsValue,
    });
  }

  public onConfirmed(result: AttachmentsResult): void {
    this._updateAttachments(result.result);
  }

  public openPreview(attachment: InputUploadValue): void {
    this.slideModal.asked({
      files: this.attachmentsValue.map(value => this._toDocument(value)),
      initial: this._toDocument(attachment),
    });
  }

  private _toDocument(attachment: InputUploadValue): DocumentDto {
    return {
      fileType: FileType.Unknown,
      id: attachment.id,
      name: attachment.name,
      uploadedDate: '',
      url: attachment.url,
    };
  }

  public remove(attachment: InputUploadValue): void {
    this._updateAttachments(this.attachmentsValue.filter(value => value.id !== attachment.id));
  }

  public iconType(attachment: InputUploadValue): CamIconType {
    switch (getFileExtension(attachment.name ?? attachment.url)) {
      case EFileExtension.PDF:
        return CamIconType.Pdf;
      case EFileExtension.Excel:
        return CamIconType.Excel;
      case EFileExtension.Word:
        return CamIconType.Doc;
      case EFileExtension.Image:
        return CamIconType.Image;
      default:
        return CamIconType.FileEmpty;
    }
  }

  private _updateAttachments(attachments: InputUploadValue[]): void {
    this.attachmentsValue = attachments;
    this.attachments.emit(attachments);
  }
}
